<template>
  <div class="test-status-view">
    <XDataTable
        v-model="items"
        class="test-status-view__table"
        :items-request="getTestStatus"
        :items-request-params="itemsRequestParams"
        :headers="headers"
        no-top
        :height="height"
        :no-elevation="!header"
        :title="tableTitle"
        :range="range"
        :custom-range="computedRange"
        :refresh="refresh"
        :no-page-controls="noPageControls"
        :items-per-page="dynamicItemsPerPage"
        :search="header"
        @loading="$emit('loading', $event)"
        @refresh="() => getTestStatusCounts()"
    >
      <template #before-search>
        <XCheckbox
            label="Show '0' Status Tests"
            v-model="showZeroStatus"
        />

        <div v-if="header && range">
          <DateTimeRange 
            v-model="dataRange" 
            :default-seconds="dataRange.seconds"
          />
        </div>
      </template>

      <template 
        v-if="header"
        #search-input-slot
      >
        <XTextField
          class="test-status-view__search-text-field"
          :value="search"
          clearable
          label="Search"
          append-icon="mdi-magnify"
          @input="(searchStr) => handleSearchUpdate(searchStr)"
        />
      </template>

      <template #above-table>
        <div class="test-status-view__header">
          <div class="test-status-view__upper-header">
            <div class="test-status-view__title">
              <span v-if="title">
                {{ title }}
              </span>

              <XBtn
                  v-if="search.length"
                  color="primary"
                  text="Clear filter"
                  icon="mdi-filter-remove"
                  @click="() => handleSearchUpdate('')"
                  small
              />
            </div>

            <div class="test-status-view__top-right-area">
              <DateTimeRange v-if="!header && range" v-model="dataRange" range-default-time="8600"/>

              <TestStatusLegend v-if="showLegend"/>
            </div>
          </div>

          <div class="test-status-view__status-bar">
            <XBtn
              class="test-status-view__total-button"
              :text="statusTotal.text"
              height="25"
              :color="statusesFilter.length ? `${statusTotal.color} lighten-1` : `${statusTotal.color}`"
              elevation="0"
              @click="() => selectTotalStatuses()"
            />

            <SplitButton
              class="test-status-view__statuses-split-btn"
              :value="statusesFilter"
              :values="statusesCounts"
              :splits="statusesRepr"
              :total="statusesCounts?.total || 0"
              multiple
              @input="(statuses) => setStatusFilter(statuses)"
            />
          </div>
        </div>
      </template>

      <template #[`item.groupName`]="{ value, row }">
        <v-hover>
          <template #default="{ hover }">
            <div class="test-status-view__group-name">
              <v-icon
                v-if="hover && header"
                class="test-status-view__search-for-name-lens"
                @click="() => handleSearchUpdate(value)"
              >
                mdi-magnify
              </v-icon>

              <router-link
                class="test-status-view__group-link"
                :to="getTestGroupPath(row.id)"
              >
                <span v-if="row.groupStartType === 1">
                  {{ parseUmlauts(value) }}
                </span>

                <span v-else>
                  {{ `${parseUmlauts(value)} (${parseUmlauts(row.user.firstName)} ${parseUmlauts(row.user.lastName)})` }}
                </span>
              </router-link>
            </div>
          </template>
        </v-hover>
      </template>

      <template #pagination-slot="{ metadata }">
        <PaginationBlock
          :total-count="metadata.totalCount || 0"
          :current-page="page"
          :items-per-page="metadata.perPage || 0"
          @update:current-page="(pageN) => setPageN(pageN)"
        />
      </template>

      <template #items-per-page-slot>
        <XSelect
          class="test-status-view__items-per-page-select"
          :items="ITEMS_PER_PAGE_OPTIONS_DEFAULT"
          :value="dynamicItemsPerPage"
          @input="(_itemsPerPage) => updateItemsPerPage(_itemsPerPage)"
          label="Items per page"
          required
          :autocomplete="false"
        />
      </template>

      <template #[`item.testsStatus`]="{ value, row }">
          <div class="test-status-view__status-bar">
            <XBtn
              class="test-status-view__total-button"
              :text="value.total"
              height="25"
              :color="statusTotal.color"
              elevation="0"
              :to="getTestGroupPath(row.id)"
            />

            <SplitButton
              class="test-status-view__statuses-split-btn"
              :values="value"
              :splits="getStatusesSplitsForTest(value)"
              :to-path="(split) => getTestGroupPath(row.id, split.value)"
            />
          </div>
      </template>
    </XDataTable>
  </div>
</template>

<script>
import { defineComponent, shallowRef, ref } from "vue"
import XDataTable from '@/components/basic/XDataTable.vue';
import TestStatusLegend from '@/commonComponents/dynamic/testStatusLegend.vue';
import DateTimeRange from '@/components/basic/DateTimeRange.vue';
import {shaKey} from '@/js/helper';
import SplitButton from '@/components/basic/SplitButton.vue';
import XBtn from '@/components/basic/XBtn.vue';
import PaginationBlock from "@/components/basic/tables/PaginationBlock.vue";
import XSelect from '@/components/basic/XSelect.vue';
import XTextField from '@/components/basic/XTextField.vue';
import XCheckbox from "@/components/basic/XCheckbox.vue";
import cockpitTestStatusService from "@/js/services/CockpitTestStatusService";
import {getUnixFromToByRange, parseUmlauts, unixToDateTimeString} from '@/js/general';
import { useStatusBarSelect } from "@/composition/filtering-components/use-status-bar-select"
import { GROUP_START_TYPES } from "@/composition/explorer-tests/config"
import { ITEMS_PER_PAGE_OPTIONS_DEFAULT, ITEMS_PER_PAGE_DEFAULT } from "@/composition/tables/use-table-settings"
import { MIN_SEARCH_LENGTH } from "@/composition/filtering-components/use-search-input-w-router"
import { debounce } from "lodash-es";

function getTestStatus(
  onlyScheduled,
  statusesAsParamString,
  showZeroStatus,
  page,
  itemsPerPage,
  search,
  params,
  ...args
) {
  params["page"] = page;
  params["items-per-page"] = itemsPerPage;
  params["search"] = search;
  cockpitTestStatusService.getTestStatus(onlyScheduled, statusesAsParamString, showZeroStatus, params, ...args);
}

const DEFAULT_SECONDS = 24 * 60 * 60;

export default defineComponent({
  name: 'TestStatus',

  components: {
    XCheckbox,
    SplitButton,
    XBtn,
    DateTimeRange,
    XDataTable,
    TestStatusLegend,
    PaginationBlock,
    XSelect,
    XTextField,
  },

  props: {
    title: String,
    range: Boolean,
    customRange: Object,
    refresh: Number,
    noPageControls: Boolean,
    itemsPerPage: Number,
    showTestName: {
      type: Boolean,
      default: true,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    searchText: String,
    height: [Number, String],
    header: Boolean,
    onlyScheduled: Boolean,
  },

  setup(props) {
    const {
      statusesCounts,
      statusesFilter,
      statusesRepr,
      statusTotal,
      generateSplits,
      selectTotalStatuses: _selectTotalStatuses,
      statusesAsParamString,
    } = useStatusBarSelect({
      multiple: true,
    });

    const page = ref(1);
    const setPageN = (pageN) => {
      page.value = pageN;
    };
    const selectTotalStatuses = () => {
      setPageN(1);
      _selectTotalStatuses();
    };
    const setStatusFilter = (statuses) => {
      setPageN(1);
      statusesFilter.value = statuses;
    };
    const dynamicItemsPerPage = ref(props.itemsPerPage || ITEMS_PER_PAGE_DEFAULT);
    const updateItemsPerPage = (_itemsPerPage) => {
      if (props.noPageControls) {
        return;
      }

      if (_itemsPerPage > dynamicItemsPerPage.value) {
        setPageN(1);
      }
      dynamicItemsPerPage.value = _itemsPerPage;
    };

    const search = ref(props.searchText || "");


    const defaultSplits = shallowRef(generateSplits({
      getText: () => void 0,
    }))

    const getStatusesSplitsForTest = (testsStatus) => {
      return defaultSplits.value.filter(s => Boolean(testsStatus[s.key]))
    };

    const showZeroStatus = ref(false)

    return {
      selectTotalStatuses,
      statusesCounts,
      defaultSplits,
      statusesFilter,
      statusesRepr,
      statusTotal,
      statusesAsParamString,
      getStatusesSplitsForTest,
      getTestStatus,
      page,
      dynamicItemsPerPage,
      setPageN,
      updateItemsPerPage,
      search,
      setStatusFilter,
      showZeroStatus,

      ITEMS_PER_PAGE_OPTIONS_DEFAULT,
    }
  },

  data() {
    return {
      customHeight: 200,
      items: [],
      dataRange: {
        from: new Date(new Date().getTime() - DEFAULT_SECONDS * 1000),
        to: new Date(),
        seconds: DEFAULT_SECONDS,
      },
      statusFilter: -1,
    };
  },

  watch: {
    computedRange() {
      this.setPageN(1);
      this.getTestStatusCounts();
    },
  },

  created() {
    this.getTestStatusCounts();
  },

  computed: {
    itemsRequestParams() {
      return [
        !this.onlyScheduled ? -1 : 1, // -1 stands for nothing, 1 stands for scheduled
        this.statusesAsParamString,
        this.showZeroStatus,
        this.page,
        this.dynamicItemsPerPage,
        this.search,
      ];
    },

    headers() {
      const headers = [
        {
          text: 'Start',
          value: 'startTime',
          formatter: (value) => {
            return unixToDateTimeString(value);
          },
          width: 154,
        },
        {
          text: 'Type',
          value: 'groupStartType',
          width: 100,
          formatter: (value) => {
            return GROUP_START_TYPES[value];
          },
        },
        {
          text: 'Name',
          value: 'groupName',
          width: 500,
        },
        {
          text: 'Status',
          value: 'testsStatus',
          width: '100%',
        },
      ];

      if (this.onlyScheduled) {
        headers.splice(1, 1);
      }

      if (!this.showTestName) {
        headers.splice(headers.findIndex(x => x.value === 'groupName'), 1);
      }

      return headers;
    },

    tableTitle() {
      if (this.header) return 'Test Status';
      return undefined;
    },

    computedRange() {
      if (this.customRange && !this.range) return this.customRange;
      return this.dataRange;
    },
  },

  methods: {
    parseUmlauts,

    getTestGroupPath(groupId, status = -1) {
      const testQueueId = this.items.find(x => x.id === groupId).testQueueId;
      if (testQueueId) {
        return {
          name: "test-info",
          params: {
            id: testQueueId,
            idKey: shaKey(testQueueId.toString()),
          }
        };
      }

      const route = {
        name: "test-group",
        params: {
          id: groupId,
        }
      }
      if (status > 0) {
        route.query = { status };
      }
      return route;
    },

    async getTestStatusCounts() {
      const fromTo = getUnixFromToByRange(this.computedRange);
      this.statusesCounts = await cockpitTestStatusService.getTestStatusCounts(fromTo.from, fromTo.to, this.search)
    },

    handleSearchUpdate: debounce(async function(searchStr) {
      this.search = searchStr;
      if ((searchStr.length && searchStr.length >= MIN_SEARCH_LENGTH) || this.search.length < MIN_SEARCH_LENGTH && !searchStr.length) {
        this.setPageN(1);
        await this.getTestStatusCounts();
      }
    }, 500),
  },
});
</script>

<style lang="scss">
.test-status-view {
  height: 100%;

  &__search-text-field {
    flex: 1 1 auto;
    max-width: 350px;
  }

  &__table {
    height: 100%;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__upper-header {
    display: flex;
    gap: 10px;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    font-size: 18px;
  }

  &__total-button {
    flex: 0 0 auto;

    .v-btn:not(.v-btn--round).v-size--default {
      color: var(--v-text-inverted-base);
      font-size: 12px;
    }
  }

  &__status-bar {
    display: flex;
    width: 100%;
    gap: 2px;
    align-items: center;
  }

  &__statuses-split-btn {
    flex: 1;
  }

  p {
    font-family: sans-serif;
  }

  &__group-name {
    position: relative;
  }

  &__search-for-name-lens.v-icon {
    position: absolute;
    left: -24px;
    cursor: pointer;
  }

  &__top-right-area {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  &__items-per-page-select {
    width: 120px;
    flex-shrink: 0;
  }

}
</style>
